import { PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

import { Heading } from '@dbt-labs/sourdough';

import { SearchInput } from '@/components/SearchInput';
import { useEnvironmentSwitcher } from '@/hooks/useEnvironmentSwitcher';
import { NavButton } from './NavButton/NavButton';

interface PanelHeaderParams extends PropsWithChildren<{}> {
  projectName: string;
  productionEnv: number;
  className?: string;
  stagingEnv?: number;
  devEnv?: number;
}

export const PanelHeader: React.FC<PanelHeaderParams> = ({
  projectName,
  productionEnv,
  stagingEnv,
  devEnv,
  className,
  children,
}) => {
  const {
    productionUrl,
    stagingUrl,
    devUrl,
    selectedEnv,
    allowDevEnvToggle,
    isProductionEnabled,
    isStagingEnabled,
  } = useEnvironmentSwitcher(productionEnv, stagingEnv, devEnv);

  const envButtonClasses = 'flex flex-1 rounded items-center justify-center';

  return (
    <div className={twMerge(selectedEnv, 'space-y-5', className)}>
      <div className="flex-0 border-b border-b-borderMuted pb-2">
        <div className="mb-4 space-x-1">
          <Heading component="h1" size={'6'} children={projectName} />
        </div>
        {(stagingEnv || allowDevEnvToggle) && (
          <div className="mb-4 flex h-8 flex-row bg-bgNeutralTinted">
            <NavButton
              id="production"
              className={twMerge(
                envButtonClasses,
                selectedEnv === 'prodEnv' && 'border border-borderInfoMuted',
              )}
              to={productionUrl}
              isSelected={selectedEnv === 'prodEnv'}
              isDisabled={!isProductionEnabled}
            >
              Production
            </NavButton>
            {stagingEnv && (
              <NavButton
                id="staging"
                className={twMerge(
                  envButtonClasses,
                  selectedEnv === 'stagingEnv' && 'border border-borderSuccessMuted',
                )}
                to={stagingUrl}
                isSelected={selectedEnv === 'stagingEnv'}
                isDisabled={!isStagingEnabled}
              >
                Staging
              </NavButton>
            )}
            {allowDevEnvToggle && (
              <NavButton
                id="dev"
                className={twMerge(
                  envButtonClasses,
                  selectedEnv === 'devEnv' && 'border border-borderInfoAltMuted',
                )}
                to={devUrl}
                isSelected={selectedEnv === 'devEnv'}
              >
                Dev
              </NavButton>
            )}
          </div>
        )}
        <SearchInput />
      </div>
      {children}
    </div>
  );
};
